import { useRef, useMemo, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT } from './constants';
import type { Position } from '@/types/game';

interface ParticleSystem3DProps {
  totalCols: number;
  totalRows: number;
  playerPosition: Position;
  hasShield?: boolean;
}

// Confetti burst on level complete
function ConfettiBurst({ totalCols, totalRows }: { totalCols: number; totalRows: number }) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const elapsed = useRef(0);
  const count = 50;

  const particles = useMemo(() => {
    return Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * totalCols * TILE_SIZE,
      z: (Math.random() - 0.5) * totalRows * TILE_SIZE,
      vy: 2 + Math.random() * 3,
      vx: (Math.random() - 0.5) * 2,
      vz: (Math.random() - 0.5) * 2,
      color: new THREE.Color().setHSL(Math.random(), 0.8, 0.6),
      rotSpeed: (Math.random() - 0.5) * 10,
    }));
  }, [totalCols, totalRows]);

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useFrame((_, delta) => {
    if (!meshRef.current) return;
    elapsed.current += delta;

    particles.forEach((p, i) => {
      const t = elapsed.current;
      const gravity = -5;
      const y = FLOOR_HEIGHT + p.vy * t + 0.5 * gravity * t * t;

      if (y < -1) return;

      dummy.position.set(
        p.x + p.vx * t,
        y,
        p.z + p.vz * t,
      );
      dummy.rotation.set(t * p.rotSpeed, t * p.rotSpeed * 0.7, 0);
      dummy.scale.setScalar(Math.max(0, 1 - t * 0.3));
      dummy.updateMatrix();
      meshRef.current!.setMatrixAt(i, dummy.matrix);
      meshRef.current!.setColorAt(i, p.color);
    });
    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
      <boxGeometry args={[0.06, 0.06, 0.06]} />
      <meshStandardMaterial />
    </instancedMesh>
  );
}

// Shield sphere around character
function ShieldSphere({ position, totalCols, totalRows }: { position: Position; totalCols: number; totalRows: number }) {
  const meshRef = useRef<THREE.Mesh>(null);

  const x = (position[0] - (totalCols - 1) / 2) * TILE_SIZE;
  const z = (position[1] - (totalRows - 1) / 2) * TILE_SIZE;
  const y = FLOOR_HEIGHT + 0.3;

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const mat = meshRef.current.material as THREE.MeshStandardMaterial;
    mat.opacity = 0.15 + Math.sin(clock.getElapsedTime() * 3) * 0.1;
  });

  return (
    <mesh ref={meshRef} position={[x, y, z]}>
      <sphereGeometry args={[0.5, 16, 16]} />
      <meshStandardMaterial
        color="#4488FF"
        emissive="#4488FF"
        emissiveIntensity={0.3}
        transparent
        opacity={0.2}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

export default function ParticleSystem3D({ totalCols, totalRows, playerPosition, hasShield }: ParticleSystem3DProps) {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    const handleLevelComplete = () => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    };
    window.addEventListener('levelComplete', handleLevelComplete);
    return () => window.removeEventListener('levelComplete', handleLevelComplete);
  }, []);

  return (
    <>
      {showConfetti && <ConfettiBurst totalCols={totalCols} totalRows={totalRows} />}
      {hasShield && <ShieldSphere position={playerPosition} totalCols={totalCols} totalRows={totalRows} />}
    </>
  );
}
